import React, { useMemo } from 'react';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import { useGolfLeagueContext } from '../context/GolfLeagueContext';
import TeamCardHeader from './TeamCardHeader';
import PlayerCard from './PlayerCard';
import { Box, Typography } from '@mui/material';
import GolfScoreTable from './GolfScoreTable';
import { getRankSuffix } from '../../../utils/GetRankSuffix';
import SelectionBar from '../../NavigationBars/MyPicksNavigation/SelectionBar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import  { useNavigate } from 'react-router-dom';

const RosterPage = () => {
    const {
        players,
        teams,
        events,
        selectedTeamId,
        intervalsToShow,
        findTournamentId,
        findTournamentInfo,
    } = useGolfLeagueContext();
    //console.log("Selected Team ID", selectedTeamId, teams);

    const {
        masterPoolData,
        masterPoolId,
        gameAddress,
        gameId,
    } = useUserAndGameContext();

    const navigate = useNavigate();

    const selectedTeam = useMemo(() => {
        return teams?.find(team => team.teamId === Number(selectedTeamId));
    }, [teams, selectedTeamId]);
    //console.log("Selected Team", selectedTeam);
    const selectedTeamPlayers = useMemo(() => {
        return selectedTeam?.roster;
    }, [selectedTeam]);
    //console.log("Selected Team Players", selectedTeamPlayers);
    const selectedPlayers = useMemo(() => {
        // Map through players and find where playerId is in selectedTeamPlayers
        const filteredPlayers = players?.filter(player => selectedTeamPlayers?.includes(player.playerId));
        // Sort the filtered players by their scores.overall.rank
        // if they don't have a rank, put them at the end and if tied andyone with captain = true is first
        const sortedPlayers = filteredPlayers?.sort((a, b) => {
            const rankA = a.scores?.overall?.rank || Infinity;
            const rankB = b.scores?.overall?.rank || Infinity;
            const captainA = a.captain ? 0 : 1;
            const captainB = b.captain ? 0 : 1;

            if (rankA === rankB) {
                return captainA - captainB;
            }
            return rankA - rankB;
        }
        );
        return sortedPlayers;
    }, [players, selectedTeamPlayers]);
    //console.log("Selected Players", selectedPlayers);


    const rosterPageInfo = masterPoolData?.pageInfo?.roster;
    const rosterPageTitle = rosterPageInfo?.pageTitle || "The Roster";
    const textColor = rosterPageInfo?.textColor || "#002129";
    const secondTitle = rosterPageInfo?.secondTitle || "Team Results";
    const playerScoreText = rosterPageInfo?.playerScoreText;

    const teamOverallRank = selectedTeam?.scores?.overall?.rank;
    const teamOverallScore = selectedTeam?.scores?.overall?.score;
    const teamOverallTied = selectedTeam?.scores?.overall?.tied || false;
    //console.log("Team Tied", teamOverallTied, selectedTeam?.scores?.overall);
    const formattedScore = teamOverallScore === 0 ? "E" : 
                            teamOverallScore > 0 ? `+${teamOverallScore}` : 
                            teamOverallScore;
    const formattedRank = getRankSuffix(teamOverallRank);

    const teamRank = `${teamOverallTied ? 'T-' : ''}${formattedRank} Place: (${formattedScore})`;
    const teamName = selectedTeam?.teamName;
    const teamLogo = selectedTeam?.teamLogo;

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    };

    const handleBackClick = () => {
        navigate(`/${uriEncoded(gameAddress)}?mId=${uriEncoded(masterPoolId)}&gId=${uriEncoded(gameId)}&page=teams`);
    };

    let teamScores = [];
    if (events && selectedTeam && intervalsToShow) {
        intervalsToShow?.forEach((interval) => {
            const tournamentId = findTournamentId(events, interval);
            const event = events?.find((event) => event.interval === interval);
            const tournamentInfo = findTournamentInfo(selectedTeam, tournamentId);
            //console.log("Tournament Info", tournamentInfo, "Tournament ID", tournamentId, "Interval", interval);
            if (tournamentId) {
                teamScores.push({
                    eventName: event.name,
                    score: tournamentInfo?.score ?? tournamentInfo?.status ?? '-',
                    tied: tournamentInfo?.tied,
                    round1: tournamentInfo?.round1 || tournamentInfo?.status || '-',
                    round2: tournamentInfo?.round2 || tournamentInfo?.status || '-',
                    status: tournamentInfo?.status,
                    rank: tournamentInfo?.rank
                });
            }
        })
    }
    //console.log("Team Scores!!", teamScores);


    return (
        <Box>
            <SelectionBar
                selectedId={selectedTeamId}
                items={teams}
                idKey="teamId"
                nameKey="teamName"
                urlParamKey="teamId"
                onChange={(newTeamId) => {
                    // If you need to handle the change event beyond URL updates
                    console.log("Team selection changed to:", newTeamId);
                    // Any additional logic you need
                }}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'left',
                    marginLeft: '16px',
                    cursor: 'pointer',
                    padding: '16px 16px',
                    maxWidth: '600px',
                    margin: '0 auto',
                }}
                onClick={handleBackClick}
            >
                <ArrowBackIosNewIcon sx={{ fontSize: '14px', marginTop: '3px'}} />
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: '700',
                        marginLeft: '8px',
                        color: textColor,
                    }}
                >
                        Back to Teams Page
                    </Typography>
            </Box>

            <Box
                sx={{
                    width: '80%',
                    maxWidth: '600px',
                    margin: '0 auto',
                    padding: '16px 0px',
                }}
            >
                <TeamCardHeader 
                    teamName={teamName}
                    teamRank={teamRank}
                    logoUrl={teamLogo}
                />
            </Box>

            <Typography
                sx={{
                    fontSize: '40px',
                    fontWeight: '800',
                    margin: '24px 0',
                    textAlign: 'center',
                    color: textColor,
                }}
            >
                {rosterPageTitle}
            </Typography>

            {selectedPlayers?.map((player, index) => (
                <PlayerCard
                    key={index}
                    events={events}
                    player={player}
                    teamLogo={teamLogo}
                    playerScoreText={playerScoreText}
                    showScoresButton={true}
                    intervalsToShow={intervalsToShow}
                    findTournamentId={findTournamentId}
                    findTournamentInfo={findTournamentInfo}
                />
            ))}

            <Typography
                sx={{
                    fontSize: '40px',
                    fontWeight: '800',
                    margin: '24px 0',
                    textAlign: 'center',
                    color: textColor,
                }}
            >
                {secondTitle}
            </Typography>

            <GolfScoreTable 
                scores={teamScores}
            />

            <Box
                sx={{
                    width: '128px',
                    height: '128px',
                    justifyContent: 'center',
                    margin: '24px auto 32px auto',
                }}
            >
                <img 
                    src={teamLogo}
                    alt="Team Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
            </Box>

        </Box>
    );
}

export default RosterPage;